import React from 'react';
import { Building2, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Property } from '../../types/property';
import { PropertyActions } from './PropertyActions';
import { PropertyStatusBadge } from './PropertyStatusBadge';
import { PropertyPreviewButton } from './PropertyPreview/PropertyPreviewButton';

interface PropertyCardProps {
  property: Property;
}

export const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const navigate = useNavigate();

  // Vérifier que la propriété et ses photos existent
  if (!property) {
    return null;
  }

  // S'assurer que photos est un tableau
  const photos = Array.isArray(property.photos) ? property.photos : [];

  console.log('PropertyCard - Property:', property.id, 'Photos:', photos, 'Raw photos:', property.photos);

  const handleCardClick = (e: React.MouseEvent) => {
    // Empêcher la navigation si on clique sur le menu d'actions
    if ((e.target as HTMLElement).closest('[data-property-actions]')) {
      return;
    }
    
    // Naviguer vers le formulaire de modification
    navigate(`/modifier/${property.id}`);
  };

  return (
    <div 
      className="group relative overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-800 cursor-pointer hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
      onClick={handleCardClick}
    >
      <div className="absolute right-2 top-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity" data-property-actions>
        <div className="flex gap-2">
          <PropertyPreviewButton property={property} />
          <PropertyActions propertyId={property.id.toString()} currentStatus={property.status} />
        </div>
      </div>

      <div className="aspect-video relative">
        <div className="absolute left-2 top-2 z-10">
          <PropertyStatusBadge 
            status={property.status} 
            isPublished={property.is_published === 1 || property.is_published === true || property.isPublished === true}
          />
        </div>
        {photos.length > 0 && photos[0] ? (
          <img
            src={photos[0]}
            alt={property.type}
            className="h-full w-full object-cover"
            onError={(e) => {
              console.error('Erreur de chargement d\'image:', photos[0], 'Property:', property.id);
              e.currentTarget.style.display = 'none';
              // Afficher l'image par défaut
              const parent = e.currentTarget.parentElement;
              if (parent) {
                const defaultDiv = parent.querySelector('.default-image');
                if (defaultDiv) {
                  (defaultDiv as HTMLElement).style.display = 'flex';
                }
              }
            }}
          />
        ) : (
          <div className="default-image flex h-full w-full items-center justify-center bg-gray-200 dark:bg-gray-700">
            <Building2 className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>

      <div className="p-4 sm:p-3">
        <div className="flex items-start justify-between">
          <h3 className="text-base sm:text-sm font-medium text-gray-900 dark:text-white">
            {property.type}
          </h3>
          <p className="text-base sm:text-sm font-bold text-primary-500">
            {Math.floor(property.price || 0).toLocaleString('fr-FR').replace(/,/g, ' ')} DA
          </p>
        </div>
        <div className="mt-2 sm:mt-1 flex items-center justify-between text-sm sm:text-xs text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4 sm:h-3 sm:w-3" />
            <span>{property.address}</span>
          </div>
        </div>
        <div className="mt-3 sm:mt-2 flex gap-4 text-sm sm:text-xs text-gray-600 dark:text-gray-400">
          <span>{Math.floor(property.usable_area || property.area || 0)} m²</span>
          <span>{property.rooms || property.features?.rooms || 0} pièces</span>
          {(property.floor !== undefined || property.features?.floor !== undefined) && (
            <span>{property.floor || property.features?.floor}e étage</span>
          )}
          <span className="ml-auto rounded-full bg-gray-100 px-3 py-1 sm:px-2 sm:py-0.5 text-sm sm:text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            {property.city}
          </span>
        </div>
      </div>
    </div>
  );
};